import { DynamicToolUIPart } from 'ai';
import {
    AiChatMessageToolCallDto,
    AiChatThreadMessageDto,
    AiToolOutputSummary,
    PortalChatMessage,
    ToolCallDebugInfo,
} from 'types/aiChat';

const mapToolCallToPart = (toolCall: AiChatMessageToolCallDto): DynamicToolUIPart => {
    const status = (toolCall.status || '').toUpperCase();

    if (['ERROR', 'FAILED', 'FAIL'].includes(status)) {
        return {
            type: 'dynamic-tool',
            toolName: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            state: 'output-error',
            input: toolCall.inputPayload || {},
            errorText: toolCall.errorText || 'Tool execution failed',
        };
    }

    if (['DENIED', 'REJECTED'].includes(status)) {
        return {
            type: 'dynamic-tool',
            toolName: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            state: 'output-denied',
            input: toolCall.inputPayload || {},
            approval: {
                id: toolCall.toolCallId,
                approved: false,
            },
        };
    }

    if (['OK', 'SUCCESS', 'SUCCEEDED', 'COMPLETED', 'DONE'].includes(status)) {
        return {
            type: 'dynamic-tool',
            toolName: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            state: 'output-available',
            input: toolCall.inputPayload || {},
            output: toolCall.outputPayload || { status: 'OK' },
        };
    }

    if (['PENDING', 'RUNNING', 'IN_PROGRESS', 'STARTED', 'QUEUED'].includes(status)) {
        return {
            type: 'dynamic-tool',
            toolName: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            state: 'input-available',
            input: toolCall.inputPayload || {},
        };
    }

    // Unknown legacy statuses are treated as completed to avoid false "pending" pulses in history.
    return {
        type: 'dynamic-tool',
        toolName: toolCall.toolName,
        toolCallId: toolCall.toolCallId,
        state: 'output-available',
        input: toolCall.inputPayload || {},
        output: toolCall.outputPayload || { status: status || 'UNKNOWN' },
    };
};

/** A stored call's summary, as the live stream sends it in its output; none when the call stored none. */
const toSummary = (toolCall: AiChatMessageToolCallDto): AiToolOutputSummary | null =>
    toolCall.recordCount == null && toolCall.partialResults == null && toolCall.totalCount == null && !toolCall.counts
        ? null
        : {
              count: toolCall.recordCount ?? null,
              partial: toolCall.partialResults === true,
              total: toolCall.totalCount ?? null,
              ...(toolCall.counts ? { counts: toolCall.counts } : {}),
          };

export const mapThreadMessagesToUiMessages = (messages: AiChatThreadMessageDto[]): PortalChatMessage[] => {
    // The question an answer replies to: the turn runs from its insert to the answer's.
    let questionCreatedAt: string | undefined;
    return messages.map((message) => {
        const isAssistant = message.role === 'ASSISTANT';
        const turn =
            isAssistant && questionCreatedAt
                ? { turnStartedAt: questionCreatedAt, turnFinishedAt: message.createdAt }
                : {};
        if (message.role === 'USER') {
            questionCreatedAt = message.createdAt;
        }
        const toolSummaries = Object.fromEntries(
            (message.toolCalls || []).flatMap((tc) => {
                const summary = toSummary(tc);
                return summary ? [[tc.toolCallId, summary]] : [];
            })
        );

        const toolDebug = (message.toolCalls || []).reduce(
            (acc, tc) => {
                if (tc.filter != null || tc.recordCount != null || tc.inputBytes != null || tc.outputBytes != null) {
                    acc[tc.toolCallId] = {
                        toolName: tc.toolName,
                        filter: tc.filter,
                        recordCount: tc.recordCount,
                        inputBytes: tc.inputBytes,
                        outputBytes: tc.outputBytes,
                    };
                }
                return acc;
            },
            {} as Record<string, ToolCallDebugInfo>
        );

        return {
            id: `srv-${message.id}`,
            role: message.role === 'USER' ? 'user' : 'assistant',
            metadata: {
                createdAt: message.createdAt,
                ...(isAssistant ? { aiChatMessageId: message.id } : {}),
                ...turn,
                ...(Object.keys(toolDebug).length > 0 ? { toolDebug } : {}),
                ...(Object.keys(toolSummaries).length > 0 ? { toolSummaries } : {}),
            },
            parts: [
                ...(message.content
                    ? [
                          {
                              type: 'text' as const,
                              text: message.content,
                              state: 'done' as const,
                          },
                      ]
                    : []),
                ...((message.toolCalls || []).map(mapToolCallToPart) as PortalChatMessage['parts']),
            ],
        };
    });
};

export const getLatestUserText = (messages: PortalChatMessage[]): string => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const message = messages[i];
        if (message.role !== 'user') continue;

        const text = message.parts
            .filter((part): part is Extract<typeof part, { type: 'text' }> => part.type === 'text')
            .map((part) => part.text)
            .join('\n')
            .trim();

        if (text) return text;
    }

    return '';
};
